package de.oevbs.kunde.kundenportal2

import de.oevbs.businesspartner.bpcrmservice2.common.model.LineOfBusiness
import de.oevbs.businesspartner.bpcrmservice2.contact.model.Complaint
import de.oevbs.kunde.kundenportal2.model.services.contract.CarContract
import de.oevbs.kunde.kundenportal2.model.services.deregistercar.VehicleAction
import de.oevbs.kunde.kundenportal2.model.services.lifeValueNotification.LifeContract

data class PortalContact(
    val note: PortalContactNote = PortalContactNotes.Empty(),
    val contractNumber: String = "",
    val claimNumber: String = "",
    val subject: String = "",
    val lineOfBusiness: LineOfBusiness = LineOfBusiness.Unbekannt,
    val complaint: Complaint? = null
)

interface PortalContactNote {
    val note: String
}

sealed class PortalContactNotes(
    override val note: String = ""
) : PortalContactNote {

    class Empty : PortalContactNotes() {
        override val note = ""
    }

    class ConsentWasUpdated() : PortalContactNotes() {
        override val note = """
            |Ihre Einwilligungs-Optionen haben wir erfasst, vielen Dank.
            |
            |Ihr:e Berater:in ist gern für Sie da.
            |Bitte geben Sie bei Rückfragen Ihre Partnernummer an.
        """.trimMargin()
    }

    class DeliveryMethodChanged(deliveryMethod: String) : PortalContactNotes() {
        override val note = """
            |Vielen Dank. Ihren bevorzugten Versandweg haben wir wie folgt erfasst:
            |
            |$deliveryMethod
            |
            |Ihr:e Berater:in ist gern für Sie da.
            |Bitte geben Sie bei Rückfragen Ihre Partnernummer an.
        """.trimMargin()
    }

    class AccountPasswordChanged : PortalContactNotes() {
        override val note = """
            |Ihr Passwort für 'Meine Öffentliche' wurde erneuert.                         
        """.trimMargin()
    }

    class AccountDeleted : PortalContactNotes() {
        override val note = """
            |Ihr Account von 'Meine Öffentliche' wurde gelöscht.                         
        """.trimMargin()
    }

    class MessageSent(message: String) : PortalContactNotes() {
        override val note = """
            |Ihre Mitteilung wird weitergeleitet, vielen Dank.
            |Bitte geben Sie bei Rückfragen Ihre Partnernummer an.
            |
            |Ihre Mitteilung:            
            |$message 
        """.trimMargin()
    }

    class AdvisorMessageSent(message: String) : PortalContactNotes() {
        override val note = """
            |Ihre Mitteilung wird weitergeleitet, vielen Dank.
            |
            |Ihr:e Berater:in wird sich schnellstmöglich bei Ihnen melden.
            |Bitte geben Sie bei Rückfragen Ihre Partnernummer an.
            |   
            |Ihre Mitteilung:
            |'$message' 
        """.trimMargin()
    }

    class ContractMessageSent(contract: String, message: String) : PortalContactNotes() {
        override val note = """
            |Ihre Mitteilung wird weitergeleitet, vielen Dank.
            |Bitte geben Sie bei Rückfragen Ihre Partnernummer an.
            |        
            |Ihre Mitteilung zum Vertrag $contract:
            |'$message' 
        """.trimMargin()
    }

    class ClaimMessageSent(claim: String, message: String) : PortalContactNotes() {
        override val note = """
            |Ihre Mitteilung wird weitergeleitet, vielen Dank.
            |Bitte geben Sie bei Rückfragen Ihre Partnernummer an.
            |        
            |Ihre Mitteilung zum Schaden $claim:
            |'$message' 
        """.trimMargin()
    }

    class ComplaintMessageSent(
        subjects: List<String>,
        reasons: List<String>,
        solution: String,
        message: String,
        contract: String = "",
        claim: String = ""
    ) : PortalContactNotes() {
        override val note = """
            |Ihre Mitteilung wird weitergeleitet, vielen Dank.
            |Bitte geben Sie bei Rückfragen Ihre Partnernummer an.  
            |
            |Gegenstand der Beschwerde: ${subjects.joinToString(", ")}
            |Grund der Beschwerde: ${reasons.joinToString(", ")}
            |Vertrag: $contract
            |Schaden: $claim
            |Lösungsvorschlag: $solution
            |        
            |Ihre Mitteilung:            
            |'$message'
        """.trimMargin()
    }

    class DigitalServiceCardSent(contract: String, licencePlate: String) : PortalContactNotes() {
        override val note = """
            |Ihre Anfrage wurde erfasst, vielen Dank. Den Link zum Download der digitalen Servicekarte für Ihr unten 
            |angegebenes Kraftfahrzeug erhalten Sie innerhalb der nächsten 24 Stunden per Mail. Über diesen Link können 
            |Sie die digitale Servicekarte auf Ihr Smartphone herunterladen. Sie können die Servicekarte auch an weitere
            |Personen weiterleiten, die Ihr Fahrzeug nutzen. Teilen Sie dazu einfach den Link in der Mail oder 
            |leiten Sie die Karte direkt aus der Wallet-App Ihres Smartphones weiter. 
            |
            |Weitere Infos zur digitalen Servicekarte erhalten Sie unter www.oeffentliche.de/servicekarte.
            |Bei Fragen sind wir gern für Sie da. Bitte geben Sie bei Rückfragen Ihre Partnernummer an.
            | 
            |Kennzeichen: $licencePlate
            |Vertragsnummer: $contract
        """.trimMargin()
    }

    class InternationalInsuranceCardSent(contract: String, licencePlate: String) : PortalContactNotes() {
        override val note = """
            |Ihre Anfrage wurde erfasst, vielen Dank. Die Internationale Versicherungskarte erhalten Sie 
            |innerhalb der nächsten 3-5 Tage per Post. 
            |
            |Bitte geben Sie bei Rückfragen Ihre Partnernummer an.
            | 
            |Kennzeichen: $licencePlate
            |Vertragsnummer: $contract
        """.trimMargin()
    }


    class LifeValueNotificationRequestedAllContracts(contracts: List<LifeContract>) : PortalContactNotes() {
        private val contractsString =
            contracts.joinToString(separator = "\n") { "- ${it.contractNumber} (${it.branchGroup}, ${it.salesProduct})" }
        override val note = """
            |Ihre Anfrage wurde erfasst, vielen Dank. Die Wertstandsmitteilungen für die nachstehenden Vertragsnummern erhalten Sie 
            |innerhalb der nächsten 3-5 Tage per Post. 
            |
            |Vertragsnummern: 
            |$contractsString  
        """.trimMargin()
    }

    class LifeValueNotificationRequestedSingleContract(contract: LifeContract) : PortalContactNotes() {
        private val contractString = "${contract.contractNumber} (${contract.branchGroup}, ${contract.salesProduct})"
        override val note = """
            |Ihre Anfrage wurde erfasst, vielen Dank. Die Wertstandsmitteilung für die nachstehende Vertragsnummer erhalten Sie 
            |innerhalb der nächsten Tage per Post. 
            | 
            |Vertragsnummer: $contractString
        """.trimMargin()
    }

    class TaxRequestedAllContractsSuccessful(success: List<CarContract>, year: String) :
        PortalContactNotes() {
        private val contractsString1 =
            success.joinToString(separator = "\n") { "- ${it.contractNumberPm} (${it.branchGroup})" }
        override val note = """
            |Ihre Anfrage wurde erfasst, vielen Dank. Die Bescheinigungen für die erfolgreich angeforderten Vertragsnummern 
            |erhalten Sie in den nächsten Tagen auf Ihrem bevorzugten Versandweg. 
            | 
            |Jahr: $year
            |
            |Vertragsnummern: 
            |$contractsString1  
        """.trimMargin()
    }

    class TaxRequestedAllContracts(success: List<CarContract>, failure: List<CarContract>, year: String) :
        PortalContactNotes() {
        private val contractsString1 =
            success.joinToString(separator = "\n") { "- ${it.contractNumberPm} (${it.branchGroup})" }
        private val contractsString2 =
            failure.joinToString(separator = "\n") { "- ${it.contractNumberPm} (${it.branchGroup})" }
        override val note = """
            |Ihre Anfrage wurde erfasst, vielen Dank. Die Bescheinigungen für die erfolgreich angeforderten Vertragsnummern 
            |erhalten Sie in den nächsten Tagen auf Ihrem bevorzugten Versandweg. 
            | 
            |Jahr: $year
            |
            |Erfolgreich angefordert für Vertragsnummern: 
            |$contractsString1  
            |
            |Die untenstehenden Bescheinigungen konnten nicht angefordert werden. Sie können nur eine Bescheinigung pro Tag
            |und Vertrag anfordern. Sollen Sie heute noch keine Bescheinigung für diese Verträge angefordert haben,
            |wenden Sie sich bitte an den Kundenservice.
            |$contractsString2
        """.trimMargin()
    }

    class TaxRequestedSingleContract(contract: CarContract, year: String) : PortalContactNotes() {
        private val contractString = "${contract.contractNumberPm} (${contract.branchGroup})"
        override val note = """
            |Ihre Anfrage wurde erfasst, vielen Dank. Die Bescheinigung für die nachstehende Vertragsnummer erhalten Sie 
            |in den nächsten Tagen auf Ihrem bevorzugten Versandweg. 
            | 
            |Jahr: $year
            |Vertragsnummer: $contractString
        """.trimMargin()
    }

    class BankAccountChanged(iban: String) : PortalContactNotes() {
        override val note = """
            |Vielen Dank. Ihre IBAN haben wir wie folgt erfasst:
            |            
            |$iban
            |
            |Sollten Sie Kranken- oder Rechtschutzversicherungen bei unseren Produktpartnern haben, müssen Sie diese
            |gesondert über Ihre neue Bankverbindung informieren.
            |           
            |Ihr:e Berater:in ist gern für Sie da.
            |Bitte geben Sie bei Rückfragen Ihre Partnernummer an.
        """.trimMargin()
    }

    class MailAddressWasSynced(
        chosenMail: String
    ) : PortalContactNotes() {
        override val note = """            
            |Bei der Verknüpfung Ihrer Verträge mit Ihrem Kundenportal-Account haben Sie sich für die folgende
            |Mailadresse entschieden. Diese Adresse wird künftig für Account und Vertragsdaten verwendet:
            |        
            |$chosenMail
            |
            |Wenn Ihr bevorzugter Versandweg Mail ist, erhalten Sie die betreffenden Dokumente nun ebenfalls an diese
            |Mailadresse. Sie können Ihre Mailadresse jederzeit im Bereich "Meine Daten" ändern.
        """.trimMargin()
    }

    class ContactInformationChanged(
        salutation: String,
        title: String,
        firstname: String,
        prefix: String,
        lastname: String,
        dateOfBirth: String,
        street: String,
        streetNo: String,
        postalCode: String,
        city: String,
        phone: String,
        mobile: String,
        mail: String
    ) : PortalContactNotes() {

        private val nameLineSingleWhitespace =
            "$salutation $title $firstname $prefix $lastname".replace("\\s+".toRegex(), " ")

        override val note = """            
            |Vielen Dank. Ihre Daten haben wir wie folgt erfasst:
            |        
            |Personendaten:
            |$nameLineSingleWhitespace
            |
            |geboren am $dateOfBirth
            |
            |Adressdaten:
            |$street $streetNo
            |$postalCode $city
            |
            |Kontaktdaten:
            |Telefon: $phone
            |Mobil: $mobile
            |Mail: $mail
            |
            |Sofern sich die von Ihnen vorgenommenen Änderungen auch auf Ihre Verträge auswirken,
            |melden wir uns gesondert bei Ihnen.
            |
            |Ihr:e Berater:in ist gern für Sie da.
            |Bitte geben Sie bei Rückfragen Ihre Partnernummer an.
        """.trimMargin()
    }


    class DeregisterCar(
        vehicleAction: String,
        contractNumber: String,
        licencePlate: String,
        sellDate: String,
        salutation: String,
        firstname: String,
        lastname: String,
        street: String,
        streetNo: String,
        postCode: String,
        city: String,
        country: String
    ) : PortalContactNotes() {
        override val note =
            """
            |Vielen Dank. Ihre Daten haben wir wie folgt erfasst:
            |
            |Fahrzeugdaten:
            |Vertrag: $contractNumber
            |Kennzeichen: $licencePlate
            |Vorgang: $vehicleAction                    
            |Abmelde-/Verkaufsdatum: $sellDate                    
        ${
                if (vehicleAction == VehicleAction.SOLD.text) {
                    """
            |Käufer:in:                    
            |Anrede: $salutation
            |Vorname: $firstname
            |Nachname: $lastname
            |
            |Anschrift:
            |Straße: $street
            |Hausnummer: $streetNo
            |Postleitzahl: $postCode 
            |Stadt: $city
            |Land: $country
                    """
                } else {
                    ""
                }
            }
            |Sofern sich die von Ihnen vorgenommenen Änderungen auch auf Ihre Verträge auswirken,
            |melden wir uns gesondert bei Ihnen.
            |
            |Ihr:e Berater:in ist gern für Sie da.
            |Bitte geben Sie bei Rückfragen Ihre Partnernummer an.
            """.trimMargin()
    }

    class PinAuthLetterSent : PortalContactNotes() {
        override val note = """
            |Zur Verknüpfung Ihrer Vertragsdaten mit Ihrem Kundenportal-Account wurde ein Freischaltcode an Sie versendet. 
            |Sie erhalten diesen innerhalb der nächsten 3-5 Tage per Post. 
        """.trimMargin()
    }

    class PinAuthSuccessful : PortalContactNotes() {
        override val note = """
            |Die Verknüpfung Ihrer Vertragsdaten mit Ihrem Kundenportal-Account wurde erfolgreich abgeschlossen. 
        """.trimMargin()
    }
}
